import React from "react";

// Grid-unit paths on the StoreRouter 4×4 crossbar (dots at 0–3). They mirror
// apps/website/lib/storerouter-mark.ts: a round-bowl S that ends on the dot
// where the R begins, then the R, which forks at its junction (2,2).
const S_PATH = "M3 0L0.75 0A0.75 0.75 0 0 0 0.75 1.5L2.25 1.5A0.75 0.75 0 0 1 2.25 3L0 3";
const R_MAIN_PATH = "M0 3L0 0L2 0L3 1L2 2";
const R_FORK_PATHS = ["M2 2L0 2", "M2 2L3 3"];
const DOTS = Array.from({ length: 16 }, (_, index) => [index % 4, Math.floor(index / 4)] as const);

// Dashes are 1 long with a gap of 2 and hidden offsets of ±1.01, so no
// zero-length dash (a stray round-cap dot) lands on a path end. The erased S
// also drops to opacity 0: browsers scale dashes on its arcs imprecisely, and
// its last stroke would otherwise linger as a foot on the R's stem.
// One loop: the S draws, erases from its start as the R draws, the R forks,
// holds, then fades. Reduced motion keeps the finished R. Legacy Octogen hosts
// (`<html data-legacy-brand>`) get the original spinning pinwheel instead.
const STYLE = `
.sr-spinner-route{fill:none;stroke:currentColor;stroke-width:.34;stroke-linecap:round;stroke-linejoin:round;stroke-dasharray:1 2}
.sr-spinner-s{animation:sr-spinner-s 2.4s linear infinite}
.sr-spinner-r{animation:sr-spinner-r 2.4s linear infinite}
.sr-spinner-fork{animation:sr-spinner-fork 2.4s linear infinite}
@keyframes sr-spinner-s{0%{stroke-dashoffset:1.01;opacity:1}32%{stroke-dashoffset:0}53%{stroke-dashoffset:-1.01;opacity:1}54%,100%{stroke-dashoffset:-1.01;opacity:0}}
@keyframes sr-spinner-r{0%,32%{stroke-dashoffset:1.01;opacity:1}60%,86%{stroke-dashoffset:0;opacity:1}100%{stroke-dashoffset:0;opacity:0}}
@keyframes sr-spinner-fork{0%,60%{stroke-dashoffset:1.01;opacity:1}70%,86%{stroke-dashoffset:0;opacity:1}100%{stroke-dashoffset:0;opacity:0}}
.sr-spinner-legacy{display:none}
.sr-spinner-pinwheel{transform-box:fill-box;transform-origin:center;animation:sr-spinner-spin .75s linear infinite}
@keyframes sr-spinner-spin{to{transform:rotate(360deg)}}
:root[data-legacy-brand] .sr-spinner-mark{display:none}
:root[data-legacy-brand] .sr-spinner-legacy{display:inline}
@media (prefers-reduced-motion:reduce){
.sr-spinner-s{display:none}
.sr-spinner-r,.sr-spinner-fork{animation:none;stroke-dashoffset:0}
.sr-spinner-pinwheel{animation:none}
}`;

/** The animated StoreRouter loading mark, sized and colored by the caller (`currentColor`). */
export function StoreRouterSpinnerSvg({
  label = "Loading",
  ...props
}: Omit<React.SVGProps<SVGSVGElement>, "role"> & { label?: string }) {
  return (
    <svg
      viewBox="-0.5 -0.5 4 4"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-label={label}
      role="status"
      {...props}
    >
      <style>{STYLE}</style>
      <g className="sr-spinner-mark">
        <g fill="currentColor" fillOpacity={0.25}>
          {DOTS.map(([x, y]) => (
            <circle key={`${x},${y}`} cx={x} cy={y} r={0.16} />
          ))}
        </g>
        <path className="sr-spinner-route sr-spinner-s" pathLength={1} d={S_PATH} />
        <path className="sr-spinner-route sr-spinner-r" pathLength={1} d={R_MAIN_PATH} />
        {R_FORK_PATHS.map((d) => (
          <path key={d} className="sr-spinner-route sr-spinner-fork" pathLength={1} d={d} />
        ))}
      </g>
      <svg
        className="sr-spinner-legacy"
        x={-0.5}
        y={-0.5}
        width={4}
        height={4}
        viewBox="-1.39128 -1.58049 41.39438 41.39438"
      >
        <g className="sr-spinner-pinwheel">
          <path
            d="M4.74337 4.04475C10.2509 -1.39549 19.1258 -1.34089 24.5662 4.16652L29.2813 8.93935L22.051 16.081L20.5273 16.1327L19.369 14.8934L18.2107 16.1327L16.6311 16.0793L4.74337 4.04475Z"
            fill="#EC4899"
          />
          <path
            d="M16.5395 16.7864L16.573 17.7707L15.3336 18.9291L16.573 20.0874L16.5323 21.2856L4.05435 33.6358C-1.39128 28.1334 -1.34534 19.2582 4.15702 13.8126L8.92561 9.09319L16.5395 16.7864Z"
            fill="#3B82F6"
          />
          <path
            d="M21.5696 21.7598L33.8464 34.1885C28.3388 39.6289 19.4636 39.5743 14.0232 34.0667L9.30849 29.2939L16.9266 21.7683L18.2109 21.725L19.3692 22.9644L20.5275 21.725L21.5696 21.7598Z"
            fill="#EF4444"
          />
          <path
            d="M34.5575 4.59778C40.0031 10.1001 39.9568 18.9754 34.4544 24.421L29.6863 29.1404L22.2162 21.5927L22.1657 20.0869L23.4047 18.929L22.1657 17.7707L22.1971 16.8296L34.5575 4.59778Z"
            fill="#22C55E"
          />
        </g>
      </svg>
    </svg>
  );
}
